import { useCallback, useState } from 'react';
import {
    DEFAULT_CARD_SORT,
    isNotificationCardSort,
    type NotificationCardSort,
    type NotificationCardSortKey
} from './notificationCardSort';

const STORAGE_KEY = 'odo:dm-notifications:card-sort';
// A new key starts in its useful direction: newest, or most urgent, first.
const NEW_KEY_ORDER = 'desc';

function readStoredSort(): NotificationCardSort {
    try {
        const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        return isNotificationCardSort(parsed) ? parsed : DEFAULT_CARD_SORT;
    } catch {
        return DEFAULT_CARD_SORT;
    }
}

function writeStoredSort(sort: NotificationCardSort): void {
    try {
        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(sort));
    } catch {
        // Persistence is a convenience; a blocked or full storage keeps the in-memory choice.
    }
}

// Stored per browser, so the choice carries across patients and Prev/Next.
export function useNotificationCardSort() {
    const [sort, setSort] = useState<NotificationCardSort>(readStoredSort);

    const selectSort = useCallback(
        (key: NotificationCardSortKey) => {
            const order =
                sort.key === key ? (sort.order === 'desc' ? 'asc' : 'desc') : NEW_KEY_ORDER;
            const next: NotificationCardSort = { key, order };
            writeStoredSort(next);
            setSort(next);
        },
        [sort]
    );

    return { sort, selectSort };
}
